import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Trophy, Plus, RefreshCw, Trash2, Pencil, Crown, Medal, Flag,
    CheckCircle2, Clock, ChevronDown, ChevronUp, Zap, Repeat,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago', month: 'short', day: 'numeric',
            year: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

// ISO (UTC) → value for <input type="datetime-local"> in the browser's local tz.
const isoToLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const localInputToIso = (v) => (v ? new Date(v).toISOString() : null);

const STATUS_STYLE = {
    running: { label: 'LIVE', c: '#39FF14' },
    scheduled: { label: 'SCHEDULED', c: '#00E0FF' },
    ended: { label: 'ENDED · AWAITING PAYOUT', c: '#FFD700' },
    finalized: { label: 'FINALIZED · PAID', c: '#A855F7' },
    paused: { label: 'PAUSED', c: '#A0AAB2' },
};

const RANK_ICON = [Crown, Medal, Flag];
const RANK_COLOR = ['#FFD700', '#C0C0C0', '#CD7F32'];

const emptyForm = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
        name: '',
        start_at: isoToLocalInput(now.toISOString()),
        end_at: isoToLocalInput(end.toISOString()),
        p1: 500, p2: 250, p3: 100,
        m1t: 250, m1b: 50, m2t: 500, m2b: 100,
        active: true,
        auto_recur: false,
    };
};

export const AmbassadorCupPanel = () => {
    const [comps, setComps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [standings, setStandings] = useState({});
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/ambassador-cup`, { withCredentials: true });
            setComps(data || []);
        } catch {
            toast.error('Failed to load competitions');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyForm()); setEditId(null); setShowForm(true); };
    const openEdit = (c) => {
        const p = c.prizes || [];
        const m = c.milestones || [];
        setForm({
            name: c.name,
            start_at: isoToLocalInput(c.start_at),
            end_at: isoToLocalInput(c.end_at),
            p1: p[0]?.amount ?? 500, p2: p[1]?.amount ?? 250, p3: p[2]?.amount ?? 100,
            m1t: m[0]?.threshold ?? 250, m1b: m[0]?.bonus ?? 50,
            m2t: m[1]?.threshold ?? 500, m2b: m[1]?.bonus ?? 100,
            active: c.active,
            auto_recur: c.auto_recur,
        });
        setEditId(c.id);
        setShowForm(true);
    };

    const save = async () => {
        if (!form.name.trim() || form.name.trim().length < 2) { toast.error('Enter a competition name'); return; }
        if (!form.start_at || !form.end_at) { toast.error('Pick start and end dates'); return; }
        const payload = {
            name: form.name.trim(),
            start_at: localInputToIso(form.start_at),
            end_at: localInputToIso(form.end_at),
            prizes: [
                { rank: 1, amount: Number(form.p1) || 0 },
                { rank: 2, amount: Number(form.p2) || 0 },
                { rank: 3, amount: Number(form.p3) || 0 },
            ],
            milestones: [
                { threshold: Number(form.m1t) || 0, bonus: Number(form.m1b) || 0 },
                { threshold: Number(form.m2t) || 0, bonus: Number(form.m2b) || 0 },
            ].filter((m) => m.threshold > 0),
            active: form.active,
            auto_recur: form.auto_recur,
        };
        setSaving(true);
        try {
            if (editId) {
                await axios.patch(`${API}/admin/ambassador-cup/${editId}`, payload, { withCredentials: true });
                toast.success('Competition updated');
            } else {
                await axios.post(`${API}/admin/ambassador-cup`, payload, { withCredentials: true });
                toast.success('Competition created');
            }
            setShowForm(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.name}"? This can't be undone.`)) return;
        setBusyId(c.id);
        try {
            await axios.delete(`${API}/admin/ambassador-cup/${c.id}`, { withCredentials: true });
            toast.success('Deleted');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to delete');
        } finally { setBusyId(null); }
    };

    const finalize = async (c) => {
        if (!window.confirm(`Finalize "${c.name}" now and auto-credit all prize money into winners' balances? This can't be undone.`)) return;
        setBusyId(c.id);
        try {
            await axios.post(`${API}/admin/ambassador-cup/${c.id}/finalize`, {}, { withCredentials: true });
            toast.success('Finalized — prizes credited');
            await load();
            if (expanded === c.id) await toggleExpand(c.id, true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to finalize');
        } finally { setBusyId(null); }
    };

    const toggleExpand = async (id, force = false) => {
        if (expanded === id && !force) { setExpanded(null); return; }
        setExpanded(id);
        try {
            const { data } = await axios.get(`${API}/admin/ambassador-cup/${id}/standings`, { withCredentials: true });
            setStandings((s) => ({ ...s, [id]: data }));
        } catch {
            toast.error('Failed to load standings');
        }
    };

    return (
        <div className="card-dark p-5 mb-4" style={{ borderLeft: '3px solid #FFD700' }} data-testid="ambassador-cup-admin">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-lg font-bold text-white">Ambassador Cup</h3>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load}
                        className="h-8 border-white/10 text-[#A0AAB2] hover:text-white" data-testid="cup-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button size="sm" onClick={openCreate}
                        className="h-8 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold" data-testid="cup-new-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> New competition
                    </Button>
                </div>
            </div>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Time-boxed leaderboard. Ambassadors are ranked by commission earned in the window. When it ends,
                rank prizes + milestone bonuses are <span className="text-[#39FF14] font-semibold">auto-credited</span> into their pending balance.
            </p>

            {/* Create / edit form */}
            {showForm && (
                <div className="rounded-lg bg-[#0a0a0a] border border-[#FFD700]/30 p-4 mb-4 space-y-3" data-testid="cup-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="sm:col-span-3">
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Competition name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="October Ambassador Cup" data-testid="cup-name" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Starts</Label>
                            <Input type="datetime-local" value={form.start_at}
                                onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm" data-testid="cup-start" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Ends</Label>
                            <Input type="datetime-local" value={form.end_at}
                                onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm" data-testid="cup-end" />
                        </div>
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 text-xs text-[#A0AAB2] cursor-pointer h-9">
                                <input type="checkbox" checked={form.active}
                                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                    data-testid="cup-active" />
                                Active
                            </label>
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-xs text-[#A0AAB2] cursor-pointer" data-testid="cup-autorecur-label">
                        <input type="checkbox" checked={form.auto_recur}
                            onChange={(e) => setForm({ ...form, auto_recur: e.target.checked })}
                            data-testid="cup-autorecur" />
                        <span className="inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5 text-[#00E0FF]" /> Auto-recur — start a fresh Cup (same length &amp; prizes) the moment this one ends</span>
                    </label>

                    <div>
                        <p className="text-[10px] text-[#FFD700] uppercase tracking-wider font-bold mb-1">Rank prizes ($)</p>
                        <div className="grid grid-cols-3 gap-2">
                            {['🥇 1st', '🥈 2nd', '🥉 3rd'].map((lbl, i) => (
                                <div key={i}>
                                    <Label className="text-[10px] text-[#A0AAB2] mb-1 block">{lbl}</Label>
                                    <Input type="number" min="0" step="1"
                                        value={form[`p${i + 1}`]}
                                        onChange={(e) => setForm({ ...form, [`p${i + 1}`]: e.target.value })}
                                        className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono"
                                        data-testid={`cup-prize-${i + 1}`} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] text-[#39FF14] uppercase tracking-wider font-bold mb-1">Milestone bonuses (everyone who crosses)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">At earned ($)</Label>
                                <Input type="number" min="0" value={form.m1t}
                                    onChange={(e) => setForm({ ...form, m1t: e.target.value })}
                                    className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono" data-testid="cup-m1t" />
                            </div>
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Bonus ($)</Label>
                                <Input type="number" min="0" value={form.m1b}
                                    onChange={(e) => setForm({ ...form, m1b: e.target.value })}
                                    className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono" data-testid="cup-m1b" />
                            </div>
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">At earned ($)</Label>
                                <Input type="number" min="0" value={form.m2t}
                                    onChange={(e) => setForm({ ...form, m2t: e.target.value })}
                                    className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono" data-testid="cup-m2t" />
                            </div>
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Bonus ($)</Label>
                                <Input type="number" min="0" value={form.m2b}
                                    onChange={(e) => setForm({ ...form, m2b: e.target.value })}
                                    className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono" data-testid="cup-m2b" />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setShowForm(false)}
                            className="h-8 border-white/10 text-[#A0AAB2]" data-testid="cup-cancel">Cancel</Button>
                        <Button size="sm" onClick={save} disabled={saving}
                            className="h-8 bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold" data-testid="cup-save">
                            {saving ? 'Saving…' : editId ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Competition list */}
            {comps.length === 0 && !loading && (
                <p className="text-sm text-[#A0AAB2] italic py-4 text-center" data-testid="cup-empty">
                    No competitions yet. Spin one up to light a fire under your ambassadors.
                </p>
            )}
            <div className="space-y-2">
                {comps.map((c) => {
                    const st = STATUS_STYLE[c.status] || STATUS_STYLE.paused;
                    const data = standings[c.id];
                    const canFinalize = (c.status === 'ended') && !c.finalized;
                    return (
                        <div key={c.id} className="rounded-lg bg-[#0a0a0a] border border-white/10 overflow-hidden" data-testid={`cup-row-${c.id}`}>
                            <div className="p-3 flex items-center justify-between flex-wrap gap-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white font-bold text-sm">{c.name}</span>
                                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider"
                                            style={{ color: st.c, background: `${st.c}1a`, border: `1px solid ${st.c}55` }}
                                            data-testid={`cup-status-${c.id}`}>
                                            {st.label}
                                        </span>
                                        {c.auto_recur && (
                                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider inline-flex items-center gap-1 text-[#00E0FF] bg-[#00E0FF]/10 border border-[#00E0FF]/40"
                                                title="Auto-recurring — a new Cup starts automatically when this one ends"
                                                data-testid={`cup-autorecur-badge-${c.id}`}>
                                                <Repeat className="w-2.5 h-2.5" /> Auto
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-[#A0AAB2] mt-0.5">
                                        {fmtDate(c.start_at)} → {fmtDate(c.end_at)}
                                    </div>
                                    <div className="text-[10px] text-[#A0AAB2] mt-0.5">
                                        Prizes {c.prizes?.map((p) => fmt(p.amount)).join(' · ')}
                                        {c.milestones?.length ? ` · milestones ${c.milestones.map((m) => `${fmt(m.bonus)}@${fmt(m.threshold)}`).join(', ')}` : ''}
                                    </div>
                                    {c.finalized && c.results && (
                                        <div className="text-[10px] text-[#A855F7] font-bold mt-0.5">
                                            Paid out {fmt(c.results.total_awarded)} to {c.results.awards?.length || 0} winners
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {canFinalize && (
                                        <Button size="sm" onClick={() => finalize(c)} disabled={busyId === c.id}
                                            className="h-8 bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                                            data-testid={`cup-finalize-${c.id}`}>
                                            <Zap className="w-3.5 h-3.5 mr-1" /> Finalize & Pay
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => toggleExpand(c.id)}
                                        className="h-8 border-white/10 text-[#A0AAB2] hover:text-white" data-testid={`cup-expand-${c.id}`}>
                                        {expanded === c.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                        <span className="ml-1 text-[11px]">Standings</span>
                                    </Button>
                                    {!c.finalized && (
                                        <>
                                            <Button size="sm" variant="outline" onClick={() => openEdit(c)}
                                                className="h-8 border-white/10 text-[#00E0FF] hover:bg-[#00E0FF]/10" data-testid={`cup-edit-${c.id}`}>
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => remove(c)} disabled={busyId === c.id}
                                                className="h-8 border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid={`cup-delete-${c.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {expanded === c.id && (
                                <div className="border-t border-white/10 p-3" data-testid={`cup-standings-${c.id}`}>
                                    {!data ? (
                                        <p className="text-xs text-[#A0AAB2] py-2">Loading…</p>
                                    ) : (data.rows || []).length === 0 ? (
                                        <p className="text-xs text-[#A0AAB2] py-2 italic">No commission earned in this window yet.</p>
                                    ) : (
                                        <div className="space-y-1">
                                            {data.rows.map((r) => {
                                                const Icon = RANK_ICON[r.rank - 1] || null;
                                                return (
                                                    <div key={r.ambassador_id}
                                                        className="flex items-center gap-3 py-1.5 px-2 rounded bg-white/[0.02]"
                                                        data-testid={`cup-standing-${c.id}-${r.rank}`}>
                                                        <span className="w-6 text-center font-black text-sm"
                                                            style={{ color: RANK_COLOR[r.rank - 1] || '#A0AAB2' }}>
                                                            {Icon ? <Icon className="w-4 h-4 inline" /> : `#${r.rank}`}
                                                        </span>
                                                        <span className="text-white text-sm font-medium flex-1 truncate">
                                                            {r.name} <span className="text-[#A0AAB2] font-mono text-[11px]">· {r.code}</span>
                                                        </span>
                                                        <span className="font-mono font-black text-[#39FF14] text-sm">{fmt(r.score)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AmbassadorCupPanel;
